import { Component, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';

import { CustomEventService } from '@shared/services/custom-event.service';
import { GlobalSettingsService } from '@shared/services/global-settings.service';
import { WidgetService } from '@shared/services/widget.service';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { WidgetStatePlaceholderEnum } from '@shared/enums/widget-state-placeholder.enum';
import { WidgetTypeEnum } from '@shared/enums/widget-type.enum';

import colorMap from '@shared/variables/chart-color.variable';
import { commonChartConfig, commonChartTooltip } from '@shared/variables/echart-common.variable';

import AxisLineUtil from '@shared/utils/axis-line.util';
import { commonLineChartUtil } from '@shared/utils/common-line-chart.util';
import GenerateDataZoomUtil from '@shared/utils/data-zoom.util';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EChartOption, ECharts } from 'echarts';

@UntilDestroy()
@Component({
  selector: 'app-base-widget',
  templateUrl: './base-widget.component.html'
})
export class BaseWidgetComponent implements OnInit {

  @Input() dateFilterable: boolean;
  widgetId: string;
  widgetName: string;
  percent:string;
  widgetType: string;
  headerAddon: string;
  headerComponents: [] = [];
  widgetFooter: Widget.FooterModel;
  hasGraphic: boolean;
  selectedDate: string;
  note: string;
  url: string;
  from: number;
  showDownloadIcon: boolean;

  @Input()
  set period(period: string) {
    if (period === 'FROM') {
      this.selectedDate = this.from.toString(10);
    } else if (period === 'TODAY') {
      this.selectedDate = `${period}`;
    } else if (period) {
      this.selectedDate = `FROM_LAST_${period}`;
    }
  };
  loading: boolean;
  initialLoading: boolean;
  widgetStatePlaceholderEnum = WidgetStatePlaceholderEnum;
  showWidgetStatePlaceholder: WidgetStatePlaceholderEnum;

  widgetTypeEnum = WidgetTypeEnum;

  graphData: any;
  countData: any;

  chartOption: EChartOption;
  echartsInstance: ECharts;
  echartsColors: string[];
  countChartInstanceMap: Map<string, any> = new Map();
  countChartOptions: any[] = [];
  dateFormat: string;

  toolTipHTML: string;

  constructor(
    private customEventService: CustomEventService,
    private widgetService: WidgetService,
    private globalSettingsService: GlobalSettingsService
  ) { }

  ngOnInit(): void {
    this.dateFormat = this.globalSettingsService.dateFormat;

    this.toolTipHTML = this.headerComponents.join('<br>');

    if (this.widgetType.search(/chart/ig) !== -1) {
      this.hasGraphic = true;
    }

    this.loadWidgetData(true);

    // Subscribe to the dashboard announcements to update
    this.customEventService.eventAnnounced$.pipe(
      untilDestroyed(this)
    ).subscribe(({ action, data }) => {
      if (action === ActionTypesEnum.UPDATE_WIDGET_DATA) {
        this.loadWidgetData();
      }

      if (action === ActionTypesEnum.UPDATE_WIDGET_DATE) {
        const { id, date } = data;

        if (this.widgetId === id ) {
          this.selectedDate = date;
          this.loadWidgetData();
        }
      }

    });
  }

  loadWidgetData(initial?: boolean): void {
    this.loading = true;

    if (initial) {
      this.initialLoading = true;
    }

    this.widgetService.get(this.widgetId, this.selectedDate)
      .pipe(untilDestroyed(this), take(1))
      .subscribe(response => {
        this.loading = false;
        this.initialLoading = false;

        if (!response.data) {
          this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
          return;
        }
        this.showWidgetStatePlaceholder = undefined;

        if (response.footer) {
          this.widgetFooter = response.footer;
        }

        if (this.widgetType === this.widgetTypeEnum.COUNT) {
          this.countData = response.data.map((count, i, arr) => {
            count.calculatedClass = `count--1-of-${arr.length}`;

            if (count.chartInfo && count.chartInfo.type === 'gauge') {
              if (initial) {
                this.countChartOptions[i] = {
                  grid: {
                    bottom: 0,
                    top: 0,
                    left: 0,
                    right: 0
                  },
                  series: [{
                    type: 'gauge',
                    startAngle: 180,
                    endAngle: 0,
                    center: ['50%', '90%'],
                    radius: '150%',
                    min: 0,
                    max: 1,
                    splitNumber: 0,
                    axisLine: AxisLineUtil.generateAxisLine(count.chartInfo.data),
                    axisTick: {
                      show: false
                    },
                    axisLabel: {
                      show: false
                    },
                    splitLine: {
                      show: false
                    },
                    pointer: {
                      show: false
                    },
                    title: {
                      show: true,
                      offsetCenter: ['0%', '10%'],
                      textStyle: {
                        fontSize: 14,
                        color: '#666665',
                        fontFamily: 'Arial',
                        fontWeight: 400
                      }
                    },
                    detail: {
                      formatter: '{value}',
                      offsetCenter: [0, -20],
                      textStyle: {
                        fontSize: 22,
                        color: '#666666',
                        fontFamily: 'Arial'
                      }
                    },
                    data: [
                      {
                        value: count.chartInfo.data
                      }
                    ]
                  }]
                };
              } else {
                const series = [{
                  data: [{
                    value: count.chartInfo.data
                  }],
                  axisLine: AxisLineUtil.generateAxisLine(count.chartInfo.data)
                }];

                this.countChartInstanceMap.get(count.title.name).setOption({
                  series
                });
              }
            }

            if (count.chartInfo && count.chartInfo.type === 'line') {
              const formatter = (value): string => {
                let val = `${value[0].data} ${count.title.quantity ? count.title.quantity : ''}`;
                if (count.chartInfo.displayData) {
                  val = count.chartInfo.displayData[value[0].dataIndex];
                }
                return `<div style="text-align: center">${val}<br>${value[0].name}</div>`;
              };

              if (initial) {
                this.countChartOptions[i] = commonLineChartUtil ({
                  grid: {
                    top: 0,
                    bottom: 0,
                    right: 0,
                    left: 0
                  },
                  yAxisData: count.chartInfo.data,
                  xAxisData: count.chartInfo.date,
                  tooltipFormatter: (val) => formatter(val),
                  type: count.chartInfo.status,
                  opacity: 0.3
                });
              } else {
                this.countChartInstanceMap.get(count.title.name).setOption({
                  series: [{
                    data: count.chartInfo.data
                  }],
                  xAxis: {
                    data: count.chartInfo.date
                  },
                  yAxis: {
                    data: count.chartInfo.data
                  },
                  tooltip: {
                    formatter: (value) => formatter(value),
                  }
                });
              }
            }

            return count;
          });

          return;
        }

        this.echartsColors = response.data.length > 5 ? colorMap.get('default') : colorMap.get('short');

        // base chart options if initial loading is true
        if (initial) {
          this.chartOption = {
            backgroundColor: commonChartConfig.bg,
            color: this.echartsColors
          };
        }

        if (this.widgetType === this.widgetTypeEnum.PIE_CHART) {
          if (!response.data.length) {
            this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_AVAILABLE;
            return;
          } else {
            this.showWidgetStatePlaceholder = undefined;
          }

          this.graphData = response.data.map(data => {
            return {
              name: data.key,
              value: data.value
            };
          });

          // not to re render the chart instance and remain legend data not modified
          if (!initial) {

            const series: any = this.chartOption.series;

            if (series) {
              series[0].data = this.graphData;

              this.echartsInstance.setOption({
                series
              });
            }

            return;
          }

          // generate legend from graph Data using array destructuring to preserve immutability for the response object
          const legendData = [...response.data]
            .sort((a, b) => b.value - a.value)
            .map(data => {
              return {
                name: data.key,
                icon: 'circle'
              };
            });

          this.chartOption = {
            ...this.chartOption,
            tooltip: {
              ...commonChartTooltip,
              formatter: '{a} <br/>{b} : {c} ({d}%)'
            },
            legend: {
              type: 'scroll',
              data: legendData,
              textStyle: {
                color: commonChartConfig.textColor
              }
            },
            series: [
              {
                name: this.widgetName,
                type: 'pie',
                radius: '70%',
                center: ['50%', '60%'],
                data: this.graphData,
                itemStyle: {
                  normal: {
                    borderWidth: 0.5,
                    borderColor: 'white'
                  },
                  emphasis: {
                    shadowBlur: 10,
                    shadowOffsetX: 0,
                    shadowColor: commonChartConfig.itemHoverShadowColor
                  }
                },
                label: {
                  normal: {
                    textStyle: {
                      color: commonChartConfig.textColor
                    }
                  }
                },
                labelLine: {
                  normal: {
                    lineStyle: {
                      color: commonChartConfig.axisLineColor
                    }
                  }
                }
              }
            ]
          };
        }

        if (this.widgetType === this.widgetTypeEnum.DONUT_CHART) {

          if (!response.data.length) {
            this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_AVAILABLE;
            return;
          } else {
            this.showWidgetStatePlaceholder = undefined;
          }

          this.graphData = response.data.map(data => {
            return {
              name: data.name,
              value: data.count,
              percent: data.percent,
              description: data.description,
              color: data.color
            };
          });
          // not to re render the chart instance and remain legend data not modified
          if (!initial) {

            const series: any = this.chartOption.series;

            if (series) {
              series[0].data = this.graphData;

              this.echartsInstance.setOption({
                series
              });
            }

            return;
          }

          // generate legend from graph Data using array destructuring to preserve immutability for the response object
          const legendData = [...response.data]
            .sort((a, b) => b.count - a.count)
            .map(data => {
              return {
                name: data.name,
                percent: data.percent,
                icon: 'circle'
              };
            });

          const colorArray = response.data.map(data => data.color);

          const findDescription = (val: any): string => {
            const returnVal = this.graphData.find(item => item.name === val.name);

            return  (returnVal && returnVal.description) ? returnVal.description : '';
          };

          this.chartOption = {
            ...this.chartOption,
            color: colorArray,
            tooltip: {
              ...commonChartTooltip,
              formatter: (value: any) => `${value.name}: ${value.value} <br> ${findDescription(value)} ${value.percent + "%"} `,
              position: 'inside'
            },
            legend: {
              type: 'scroll',
              data: legendData,
              textStyle: {
                color: commonChartConfig.textColor
              }
            },
            series: [
              {
                name: this.widgetName,
                type: 'pie',
                radius: ['60%', '80%'],
                center: ['50%', '55%'],
                avoidLabelOverlap: false,
                data: this.graphData,
                itemStyle: {
                  normal: {
                    borderWidth: 0.5,
                    borderColor: 'white'
                  },
                  emphasis: {
                    shadowBlur: 8,
                    shadowOffsetX: 0,
                    shadowColor: commonChartConfig.itemHoverShadowColor
                  }
                },
                label: {
                  normal: {
                    show: false,
                    position: 'center',
                    textStyle: {
                      color: commonChartConfig.textColor
                    }
                  },
                  emphasis: {
                    show: true,
                    formatter: (value) => {
                      return `${value.name}: ${value.data.value} (${value.percent + "%"}) `
                    },
                    textStyle: {
                      fontSize: '14',
                    }
                  }
                },
                labelLine: {
                  normal: {
                    show: false,
                    lineStyle: {
                      color: commonChartConfig.axisLineColor
                    }
                  }
                }
              }
            ]
          };
        }

        if (this.widgetType === this.widgetTypeEnum.BAR_CHART) {

          // only last series item uses this label
          const seriesLabel = {
            normal: {
              show: true,
              color: '#000',
              position: 'insideLeft',
              formatter: (params) => {
                let sum = 0;

                for (const key in params.data) {
                  if (params.data.hasOwnProperty(key)) {
                    if (typeof params.data[key] === 'number' && this.echartsInstance.getOption().legend[0].selected[key] !== false) {
                      sum += params.data[key];
                    }
                  }
                }

                return sum;
              }
            }
          };

          let { data: { values, xAxis, yAxis } } = response;
          const { data: { groupBy, isStack = true, colorsArray, title } } = response;

          if (!values.length) {
            this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_AVAILABLE;
            return;
          } else {
            this.showWidgetStatePlaceholder = undefined;
          }

          values = values.map(item => {
            for (const [key, value] of Object.entries(item)) {
              if (typeof value === 'string' && value.length > 25) {
                item[key] = item[key].substring(0, 22) + '...';
              }
            }
            return item;
          });

          // decides whether bar is horizontal or vertical
          if (xAxis.type === 'category') {
            xAxis = {
              ...xAxis,
              axisTick: {
                alignWithLabel: true
              }
            };
          } else {
            xAxis = {
              ...xAxis,
              type: 'value',
              nameGap: 10,
              axisLabel: {
                formatter: (val) => {
                  if (typeof val === 'number' && val < 10 && val % 1 !== 0) {
                    return;
                  }
                  return val;
                }
              }
            };

            yAxis = {
              ...yAxis,
              axisTick: {
                alignWithLabel: true
              },
              axisLabel: {
                interval: 0
              }
            };
          }

          const [firstItem] = values;
          const filteredKeys = Object.keys(firstItem).filter(item => item !== groupBy);

          const colorFormatter = (item: any) => {
            let color: string;
            switch (item) {
              case 'Error':
                color = colorMap.get('status')[0];
                break;
              case 'Fail':
                color = colorMap.get('status')[1];
                break;
              case 'Not Completed':
              case 'Warning':
                color = colorMap.get('status')[2];
                break;
              case 'Success':
                color = colorMap.get('status')[3];
                break;
            }
            return color;
          };

          this.echartsColors = filteredKeys.length > 5 ? colorMap.get(colorsArray || 'default') : colorMap.get(colorsArray || 'short');

          let series: any[] = filteredKeys
            .map((item) => {
              return {
                type: 'bar',
                stack: isStack && 'project',
                label: {
                  normal: {
                    show: !isStack,
                    color: 'black',
                    position: 'right'
                  }
                },
                itemStyle: {
                  color: colorsArray === 'status' ? colorFormatter(item) : ''
                }
              };
            });

          if (isStack) {
            // last hidden series element to show the sum of stack at the right
            series = [...series, {
              type: 'bar',
              stack: 'project',
              label: seriesLabel,
              itemStyle: {
                normal: {
                  color: 'transparent'
                }
              }
            }];
          }

          const legendData = filteredKeys.map(item => {
            return {
              name: item,
              icon: 'circle'
            };
          });

          if (isStack) {
            filteredKeys.push('Total');
          }

          const dimensions = [
            groupBy,
            ...filteredKeys
          ];

          let source = values;
          if (isStack) {
            source = values.map(item => {
              return { ...item, 'Total': 0 };
            });
          }

          // not to re render the chart instance and remain legend data not modified
          if (!initial) {
            this.echartsInstance.setOption({
              dataset: {
                dimensions,
                source
              }
            });

            return;
          }

          this.chartOption = {
            ...this.chartOption,
            title: {
              show: !!title,
              text: title,
              left: 'center',
              textStyle: {
                fontSize: 16,
                fontWeight: 'bold'
              }
            },
            color: this.echartsColors,
            grid: {
              left: '15',
              right: '80',
              top: 80,
              containLabel: true
            },
            legend: {
              type: 'scroll',
              data: legendData,
              top: title ? 30 : 0
            },
            xAxis,
            yAxis,
            dataset: {
              // Provide data.
              dimensions,
              source
            },
            // Declare several series, each of them mapped to a
            // column of the dataset by default.
            series,
            dataZoom: GenerateDataZoomUtil.generateDataZoom({
              dataCount: isStack ? values.length : values.length * filteredKeys.length,
              orient: 'vertical',
              yAxisIndex: 0,
              showDataShadow: false,
              right: 5,
              width: 12,
              x: 16
            }),
            tooltip: {
              ...commonChartTooltip,
              trigger: 'axis',
              axisPointer: {
                type: 'shadow'
              },
              formatter: (params: any[]) => {
                return params.reduce((acc, curr) => {
                  for (const [key, value] of Object.entries(curr.data)) {
                    if (!!value && typeof value === 'number' && curr.seriesName === key) {
                      acc = acc + `${curr.marker} ${key} : ${value} <br>`;
                    }
                  }
                  return acc;
                }, '');
              }
            }
          };
        }

        if (this.widgetType === this.widgetTypeEnum.LINE_CHART) {
          if (!response.data.series.length) {
            this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_AVAILABLE;
            return;
          } else {
            this.showWidgetStatePlaceholder = undefined;
          }

          if (response.data.isIssueAgingChart) {
            this.chartOption = {
              tooltip: {
                ...commonChartTooltip,
                trigger: 'axis'
              },
              xAxis: {
                type: 'category',
                boundaryGap: false,
                data: response.data.xAxis
              },
              yAxis: {
                type: 'value'
              },
              series: [{
                data: response.data.series,
                type: 'line',
                smooth: true,
                itemStyle: {color: '#0083d9'},
                lineStyle: {color: '#0083d9'}
              }],
              dataZoom: [{
                type: 'slider',
                showDetail: false
              }],
            };
          }

          if (!response.data.multiProject) {
            const { data: { series, xAxis, legend } } = response;

            const legendData = legend.map(item => {
              return {
                name: item,
                icon: 'circle'
              };
            });

            this.chartOption = {
              tooltip: {
                ...commonChartTooltip,
                trigger: 'axis'
              },
              legend: {
                type: 'scroll',
                data: legendData
              },
              xAxis: {
                type: 'category',
                boundaryGap: false,
                data: xAxis
              },
              yAxis: {
                type: 'value'
              },
              series
            };
          } else {
            const seriesTemp = response.data.series;

            const chartGridHeight = 100;

            let chartGridTop = 40;

            const series = seriesTemp.map(item => {
              return {
                type: item.type,
                name: item.name,
                xAxisIndex: item.index,
                yAxisIndex: item.index,
                data: item.data
              };
            });

            const filteredIndex: any[] = Array.from(new Set(seriesTemp.map(item => item.index)));

            const filteredProjects: any[] = Array.from(new Set(seriesTemp.map(item => {
              return {
                name: item.name,
                icon: 'circle'
              };
            })));

            let filteredSeries = [];

            for (const item of filteredIndex) {
              filteredSeries = [...filteredSeries, seriesTemp.find(searchItem => searchItem.index === item)];
            }

            const yAxis: any[] = filteredSeries.map(item => {
              return {
                name: item.metric,
                type: 'value',
                nameLocation: 'middle',
                nameGap: '50',
                gridIndex: item.index,

                nameTextStyle: {
                  color: '#333'
                },
                axisTick: {
                  show: false
                },
                axisLabel: {
                  show: true
                }
              };
            });

            const xAxis: any[] = filteredSeries.map((item, index, array) => {
              return {
                type: 'category',
                gridIndex: item.index,
                data: response.data.xAxis,
                axisLabel: {
                  show: array.length - 1 === index
                }
              };
            });

            const grid: any[] = filteredIndex.map((item, index) => {
              if (index >= 1) {
                chartGridTop = chartGridTop + chartGridHeight + 25;
              }
              return {
                left: 90,
                right: 30,
                top: chartGridTop,
                height: chartGridHeight
              };
            });

            const legend: any = {
              type: 'scroll',
              data: filteredProjects,
              textStyle: {
                color: commonChartConfig.textColor
              }
            };

            this.chartOption = {
              animation: false,
              tooltip: {
                ...commonChartTooltip,
                extraCssText: `z-index: 9999;`,
                trigger: 'axis',
                formatter: (params): string => {
                  return (params as any[]).reduce((acc, curr, index, array) => {
                    let temp = '';
                    if (index === 0) {
                      temp = `${curr.axisValue}</br>${filteredSeries[curr.axisIndex].metric}</br>`;
                    }
                    temp = temp + `${curr.marker} ${curr.seriesName} : ${curr.value} </br>`;
                    if (array.length - 1 !== index && curr.axisIndex !== array[index + 1].axisIndex) {
                      temp = temp + `</br>${filteredSeries[array[index + 1].axisIndex].metric}</br>`;
                    }
                    return acc + temp;
                  }, '');
                }
              },
              axisPointer: {
                link: [
                  {
                    xAxisIndex: 'all'
                  }
                ]
              },
              grid,
              legend,
              xAxis,
              yAxis,
              dataZoom: [{
                type: 'slider',
                xAxisIndex: filteredIndex,
                showDetail: false
              }],
              series
            };
          }
        }

        if (this.widgetType === this.widgetTypeEnum.CUSTOM_ECHARTS) {
          this.chartOption = response.data;
        }
      }, error => {
        console.log('error while fetching widget data', error);
        this.loading = false;
        this.initialLoading = false;
        this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
      });
  }

  onChartInit($event: any, name?: string): void {
    if (name) {
      this.countChartInstanceMap.set(name, $event);
      return;
    }

    this.echartsInstance = $event;
  }

  trackByFn(index) {
    return index;
  }
}
